import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Star, Loader2, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reviews`;

/** Rate the platform. Reviews are published only after moderation. */
export default function ReviewPanel({ userId }) {
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/mine`, { params: { user_id: userId } })
      .then(({ data }) => {
        if (data.review) {
          setMine(data.review);
          setRating(data.review.rating);
          setText(data.review.text);
          setRole(data.review.role || '');
        }
      })
      .catch(() => {});
  }, [userId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(API, { rating, text, role: role || null },
        { params: { user_id: userId } });
      setMine({ rating, text, role, approved: false });
      toast.success('Thank you — your review is queued for moderation');
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg
        || err?.response?.data?.detail || 'Could not submit the review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="review-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" /> Rate InkPhD
        </CardTitle>
        <CardDescription>
          Your review helps other researchers decide. Published on the home page after moderation,
          with your name and role — never your email or your work.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {mine && (
          <p className="text-[12px] mb-3 flex items-center gap-1.5" data-testid="review-status">
            {mine.approved
              ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Your review is published.</>
              : <><Clock className="w-3.5 h-3.5 text-amber-600" /> Your review is awaiting moderation.</>}
            {' '}You can edit and resubmit it below.
          </p>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-1" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setRating(i)}
                data-testid={`review-star-${i}`} aria-label={`${i} stars`}>
                <Star className={`w-6 h-6 transition-transform hover:scale-110 ${i <= rating
                  ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-slate-600'}`} />
              </button>
            ))}
            <span className="text-[12px] text-muted-foreground ml-2">{rating} / 5</span>
          </div>
          <Textarea rows={4} required minLength={20} value={text} onChange={(e) => setText(e.target.value)}
            data-testid="review-text"
            placeholder="What did InkPhD change in how you write? Be concrete — what worked and what did not (at least 20 characters)." />
          <Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="review-role"
            placeholder="How should we describe you? e.g. PhD candidate, cardiology" />
          <Button type="submit" disabled={busy || text.trim().length < 20}
            data-testid="review-submit"
            className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
            {mine ? 'Update my review' : 'Submit review'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
